import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarDays, Link2, Unplug } from "lucide-react";
import { PageHeader } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { isNativeApp } from "@/lib/platform";
import { openExternal } from "@/lib/external";

export default function CalendarConnections() {
  const { currentOrgId } = useAuth();
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);

  const load = async () => {
    try {
      const r = await apiFetch("/integrations/calendar/connections");
      setConns(r.connections || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const connectProvider = async (provider) => {
    setConnecting(provider);
    try {
      const r = await apiFetch(`/integrations/${provider}/connect?organization_id=${currentOrgId}`);
      // Native iOS/Android: open OAuth in the system browser (SFSafariViewController /
      // Chrome Custom Tabs) so Google/Microsoft redirects + cookies work. The user is
      // instructed to return to the app after completing OAuth — the connection status
      // updates automatically on next load().
      if (isNativeApp()) {
        toast.info(`Continue with ${provider} in your browser, then return to the app.`);
        await openExternal(r.authorize_url);
        setConnecting(null);
        // Refresh after a short delay so the user's newly created connection appears
        setTimeout(load, 4000);
        return;
      }
      const w = window.open(r.authorize_url, `${provider}_oauth`, "width=560,height=720");
      const iv = setInterval(async () => {
        if (w?.closed) { clearInterval(iv); setConnecting(null); load(); }
      }, 1500);
    } catch (e) { toast.error(e.message); setConnecting(null); }
  };

  const disconnect = async (id, provider) => {
    if (!window.confirm(`Disconnect ${provider} calendar?`)) return;
    try {
      await apiFetch(`/integrations/calendar/connections/${id}`, { method: "DELETE" });
      toast.success("Disconnected");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const google = conns.find((c) => c.provider === "google");
  const outlook = conns.find((c) => c.provider === "outlook");

  return (
    <div className="space-y-6" data-testid="calendar-connections-page">
      <PageHeader title="Calendar Sync" subtitle="Connect your Google or Outlook calendar. Appointments you're assigned to sync automatically, and clients receive native calendar invitations." />

      <ProviderCard name="Google Calendar" connected={!!google} conn={google} onConnect={() => connectProvider("google")} onDisconnect={disconnect} connecting={connecting === "google"} icon={CalendarDays} testid="google" />
      <ProviderCard name="Outlook Calendar" connected={!!outlook} conn={outlook} onConnect={() => connectProvider("outlook")} onDisconnect={disconnect} connecting={connecting === "outlook"} icon={CalendarDays} testid="outlook" />

      <div className="bg-[#FAF8F5] border border-[#E8E2D9] rounded-2xl p-4 text-xs text-[#57534E] space-y-1.5">
        <p><b>How it works:</b> When you schedule an appointment in ClientDesk that's assigned to you, we create an event on your connected calendar and invite the client as an attendee. Reschedules and cancellations sync both ways.</p>
        <p>Your OAuth tokens are stored server-side and never exposed to the browser. Disconnect anytime.</p>
      </div>
    </div>
  );
}

function ProviderCard({ name, connected, conn, onConnect, onDisconnect, connecting, icon: Icon, testid, disabled }) {
  const showTeamsWarning = connected && conn?.provider === "outlook" && conn?.teams_capable === false;
  return (
    <div className="bg-white border border-[#E8E2D9] rounded-2xl p-5 flex items-start gap-4" data-testid={`cal-provider-${testid}`}>
      <div className="w-11 h-11 rounded-xl bg-[#FDE9D8] flex items-center justify-center text-[#E8712C] flex-shrink-0">
        <Icon size={20} strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{name}</div>
        {connected ? (
          <>
            <div className="text-xs text-[#57534E]">Connected as {conn.provider_account_email} · since {new Date(conn.connected_at).toLocaleDateString()}</div>
            {conn.provider === "outlook" && (
              <div className="text-xs mt-1.5" data-testid={`teams-status-${testid}`}>
                {conn.teams_capable ? (
                  <span className="inline-flex items-center gap-1 text-[#2C3A29]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#3D9970]" />
                    Microsoft Teams — Automatic meeting links available
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[#B85C1F]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E8712C]" />
                    Microsoft Teams — Requires Microsoft 365 work/school account. Personal MSAs can still create Outlook events; use a custom link, Google Meet, or Zoom for meeting URLs.
                  </span>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-xs text-[#57534E]">Not connected. Click Connect to sync your appointments.</div>
        )}
      </div>
      {connected ? (
        <Button variant="outline" className="rounded-full text-[#B02222] border-[#F0C69A]" onClick={() => onDisconnect(conn.id, conn.provider)} data-testid={`disconnect-${testid}`}>
          <Unplug size={13} className="mr-1.5" /> Disconnect
        </Button>
      ) : (
        <Button className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]" onClick={onConnect} disabled={connecting || disabled} data-testid={`connect-${testid}`}>
          <Link2 size={13} className="mr-1.5" /> {connecting ? "Connecting…" : disabled ? "Coming soon" : "Connect"}
        </Button>
      )}
    </div>
  );
}
